import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { Paginated } from '@/lib/api-types';

export type SortDir = 'asc' | 'desc';

export interface UsePaginatedListOptions {
  /** API path, e.g. '/api/customers'. */
  endpoint: string;
  /** Key the rows live under in the response payload, e.g. 'customers'. */
  entityKey: string;
  /** Initial/allow-listed sort key (must match a server `sortable` key). */
  defaultSort: string;
  /** Initial sort direction. Defaults to 'desc'. */
  defaultDir?: SortDir;
  /** Initial page size. Defaults to 50. */
  defaultLimit?: number;
  /**
   * Caller-supplied filters merged into the query string (e.g. `{ status }`).
   * Undefined/empty values are omitted. Changing these resets to page 1.
   */
  extraParams?: Record<string, string | undefined>;
  /** Optional override of the TanStack query key prefix (defaults to entityKey). */
  queryKeyPrefix?: string;
}

export interface UsePaginatedListResult<T> {
  rows: T[];
  total: number;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  setLimit: (limit: number) => void;
  sort: string;
  dir: SortDir;
  /** Toggle sort: same key flips dir; a new key selects it (default 'asc'). */
  toggleSort: (key: string) => void;
  q: string;
  setQ: (q: string) => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Owns pagination/sort/search state for a server-paginated list, builds the
 * query string, and fetches via TanStack Query. Resets to page 1 whenever
 * `q` / `sort` / `dir` / `extraParams` change. `q` is debounced ~300ms.
 */
export function usePaginatedList<T>(opts: UsePaginatedListOptions): UsePaginatedListResult<T> {
  const {
    endpoint,
    entityKey,
    defaultSort,
    defaultDir = 'desc',
    defaultLimit = 50,
    extraParams,
    queryKeyPrefix,
  } = opts;

  const [page, setPageRaw] = useState(1);
  const [limit, setLimitRaw] = useState(defaultLimit);
  const [sort, setSort] = useState(defaultSort);
  const [dir, setDir] = useState<SortDir>(defaultDir);
  const [q, setQRaw] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Debounce the search term (~300ms) → fewer requests while typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Serialize extraParams so the effect below fires on real changes only.
  const extraKey = JSON.stringify(extraParams ?? {});

  // Reset to page 1 whenever the result set changes (search / sort / filters).
  // Skip the very first run so we don't fight the initial page.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setPageRaw(1);
  }, [debouncedQ, sort, dir, extraKey]);

  function toggleSort(key: string) {
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir('asc');
    }
  }

  function buildQueryString(): string {
    const sp = new URLSearchParams();
    sp.set('page', String(page));
    sp.set('limit', String(limit));
    sp.set('sort', sort);
    sp.set('dir', dir);
    if (debouncedQ) sp.set('q', debouncedQ);
    if (extraParams) {
      for (const [k, v] of Object.entries(extraParams)) {
        if (v !== undefined && v !== '') sp.set(k, v);
      }
    }
    return sp.toString();
  }

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      queryKeyPrefix ?? entityKey,
      { page, limit, sort, dir, q: debouncedQ, extra: extraKey },
    ],
    queryFn: () =>
      apiFetch<{ data: Paginated<Record<string, T[]>> }>(`${endpoint}?${buildQueryString()}`),
  });

  const payload = data?.data;
  const rows = (payload?.[entityKey] as T[] | undefined) ?? [];

  return {
    rows,
    total: payload?.total ?? 0,
    page,
    setPage: setPageRaw,
    limit,
    setLimit: setLimitRaw,
    sort,
    dir,
    toggleSort,
    q,
    setQ: setQRaw,
    isLoading,
    isError,
    error,
  };
}
